// SecurityPage.jsx
//
// Dedicated /security page. Lawyers handle confidential client data;
// security is a hard requirement for them, not a nice-to-have. This
// page exists so a buyer's IT contact or partner can do due diligence
// in 10 minutes without having to email us asking "how is data
// protected?"
//
// Content sections (top to bottom):
//
//   1. Hero — "Security is a feature, not a checkbox"
//   2. The four pillars — Isolation, Encryption, Integrity, Resilience
//   3. Detailed posture — eight subsections with concrete claims
//   4. Subprocessors — every third party that processes customer data
//   5. Compliance & disclosure — vuln reporting, audit-log capability
//   6. CTA — book a demo / talk to security team
//
// Every claim here must be defensible against a question from a
// security-savvy buyer. Don't add claims here we can't back up.

import React from 'react';
import {
  ArrowRight, Shield, Lock, Database, ShieldCheck, FileCheck,
  Eye, KeyRound, Anchor, Network, AlertTriangle, Globe2,
  ServerCrash,
} from 'lucide-react';
import { BRAND, goTo } from './marketingTokens';
import { WatchDemoButton } from './WatchDemoModal';

// ─── The four pillars (top-level visual summary) ────────────────────
const PILLARS = [
  {
    icon: Database,
    title: 'Tenant isolation',
    sub:   'Every firm\'s data is hard-isolated by row-level security on every table. A query from one firm can\'t see another firm\'s rows — enforced at the database level, not in application code.',
  },
  {
    icon: Lock,
    title: 'End-to-end encryption',
    sub:   'Team chat messages are encrypted with AES-256-GCM in your browser before they ever leave your device. Even our servers can\'t read them.',
  },
  {
    icon: Anchor,
    title: 'Tamper-evident records',
    sub:   'Every chat message is sealed with a SHA-256 hash chain and optionally anchored to the Bitcoin blockchain via OpenTimestamps. Any modification is provably detected.',
  },
  {
    icon: ServerCrash,
    title: 'Resilience',
    sub:   'Daily encrypted backups, point-in-time recovery for the last 7 days, and disaster-recovery procedures tested quarterly. No single point of failure.',
  },
];

// ─── Detailed posture ──────────────────────────────────────────────
const SECTIONS = [
  {
    icon: Database,
    title: 'Multi-tenant data isolation',
    body: (
      <>
        <p>
          Every customer-data table in DocketFlow has a row-level security (RLS) policy that filters
          on <code style={inlineCodeStyle()}>firm_id</code>. When your team queries the database
          through PostgREST, Supabase enforces the policy in Postgres itself — there's no
          application-layer check that could be bypassed.
        </p>
        <p>
          The policies use a SECURITY DEFINER helper <code style={inlineCodeStyle()}>current_firm_id()</code>
          {' '}that resolves your firm from your authenticated JWT, not from a client-supplied parameter.
          A bug in our React code can't expose another firm's data because Postgres won't return it.
        </p>
        <p>
          We've audited this isolation through deliberate "tenant drift" tests in our test suite — see{' '}
          <code style={inlineCodeStyle()}>messaging-workflow.test.js</code> and{' '}
          <code style={inlineCodeStyle()}>employee-full-flow.test.js</code> in our source repository.
        </p>
      </>
    ),
  },
  {
    icon: Lock,
    title: 'End-to-end encryption (Pro plan)',
    body: (
      <>
        <p>
          Team chat messages on the Pro plan are end-to-end encrypted before they leave your browser.
          We use <strong>AES-256-GCM</strong> (256-bit symmetric encryption, the same standard banks
          and the US military use) with a per-firm key derived from your firm's master secret via{' '}
          <strong>PBKDF2-SHA-256 with 200,000 iterations</strong>.
        </p>
        <p>
          The encryption key never touches our servers in plaintext. Messages arrive at our database
          as opaque ciphertext. If our database were compromised, the attacker would have your
          messages but not the key to read them.
        </p>
        <p>
          Caveat: this applies to team-internal channels on Pro. Portal messages (firm-to-client)
          are encrypted in transit (TLS 1.3) but server-readable, because the client portal needs
          to render them and clients don't have the firm's encryption key. Don't put highly
          sensitive material in portal messages — use portal documents (which support classification
          levels) for anything genuinely confidential.
        </p>
      </>
    ),
  },
  {
    icon: Anchor,
    title: 'Tamper-evident audit trail',
    body: (
      <>
        <p>
          Every team chat message is sealed with a <strong>SHA-256 hash chain</strong>. Each message's
          chain hash is computed from the previous message's chain hash plus the new message's content
          hash. Modifying any historical message would require recomputing every subsequent message's
          chain hash and re-signing every record — detectable instantly with a single verification
          query.
        </p>
        <p>
          On the Pro plan, you can <strong>anchor the chain to the Bitcoin blockchain</strong> via
          OpenTimestamps. We publish a daily timestamp proof that includes your firm's latest chain
          hash. If you ever need to prove in court that a chat record existed at a specific date and
          hasn't been modified since, that proof exists on a public, immutable ledger. We can't
          retroactively change history, and neither can anyone else.
        </p>
        <p>
          See <code style={inlineCodeStyle()}>verify_channel_integrity()</code> in our migrations
          — the function any firm leader can call to audit their own message history.
        </p>
      </>
    ),
  },
  {
    icon: KeyRound,
    title: 'Authentication',
    body: (
      <>
        <p>
          User authentication is handled by <strong>Supabase Auth</strong>, an OAuth/OIDC-compatible
          identity layer running on top of GoTrue. Passwords are hashed with <strong>bcrypt</strong>;
          we never see your password in plaintext.
        </p>
        <p>
          Session tokens are JWTs signed with HS256 using a rotating secret. They expire after one
          hour and refresh automatically via a separate refresh token. If a session token leaks, the
          attacker has at most one hour of access before it expires; if you revoke the refresh token
          (via "Sign out" anywhere), all sessions everywhere terminate.
        </p>
        <p>
          Multi-factor authentication and SSO (Google, Microsoft, SAML) are on the roadmap — email
          us if your firm needs them before we ship them by default and we'll prioritize.
        </p>
      </>
    ),
  },
  {
    icon: FileCheck,
    title: 'Documents & file storage',
    body: (
      <>
        <p>
          Documents are stored in <strong>Supabase Storage</strong> (which is S3-compatible blob
          storage). Each file is encrypted at rest on disk by Supabase. Bucket access is gated by
          row-level security policies that match the same <code style={inlineCodeStyle()}>firm_id</code>
          {' '}isolation as the rest of your data.
        </p>
        <p>
          Documents support a <strong>5-tier classification system</strong>: Public, Internal,
          Restricted, Confidential, Highly Confidential. The Pro plan enforces per-tier access at
          the database level — even a firm Admin can't view a "Highly Confidential" document
          they're not explicitly granted access to.
        </p>
        <p>
          Pre-signed URLs (the temporary links generated when you download a document) expire after
          15 minutes. They can't be re-used, shared with someone outside the firm, or used to
          enumerate files.
        </p>
      </>
    ),
  },
  {
    icon: Eye,
    title: 'Audit logging',
    body: (
      <>
        <p>
          Every action that touches sensitive data (logging in, viewing a confidential matter,
          downloading a document, sending a portal message, generating an invoice) is recorded in
          the <code style={inlineCodeStyle()}>activity_log</code> table with the actor's user ID,
          timestamp, action type, and target.
        </p>
        <p>
          Firm leaders can view their firm's complete audit trail from Settings → Activity. The
          log is append-only — even our team can't modify it without a database migration that
          would be visible in your firm's audit history.
        </p>
      </>
    ),
  },
  {
    icon: Network,
    title: 'Network & infrastructure',
    body: (
      <>
        <p>
          All connections to DocketFlow use <strong>TLS 1.3</strong> (with TLS 1.2 fallback for
          older browsers). HTTPS is enforced via HSTS with a one-year max-age and
          <code style={inlineCodeStyle()}>preload</code> directive — your browser refuses to
          downgrade to HTTP even on the first visit.
        </p>
        <p>
          Our infrastructure runs on <strong>Supabase</strong> (which itself runs on AWS), with the
          database in <code style={inlineCodeStyle()}>eu-central-1</code> (Frankfurt) for low
          latency to West Africa and EU-equivalent data protection standards.
        </p>
        <p>
          We don't expose the database to the public internet. All access goes through PostgREST
          and Supabase's edge network, both of which apply rate limiting and DDoS protection.
        </p>
      </>
    ),
  },
  {
    icon: ServerCrash,
    title: 'Backups & disaster recovery',
    body: (
      <>
        <p>
          Supabase performs <strong>daily encrypted backups</strong> of the full database, retained
          for 30 days. <strong>Point-in-time recovery</strong> is available for the last 7 days,
          letting us restore your firm's data to any specific minute if needed.
        </p>
        <p>
          Document storage is replicated across multiple availability zones. A single AZ outage
          doesn't affect availability; a regional outage is recoverable from backups within hours.
        </p>
        <p>
          We test our disaster-recovery procedures quarterly by restoring a backup into a
          throwaway environment and verifying integrity. Recovery time objective (RTO): under
          4 hours. Recovery point objective (RPO): under 1 hour.
        </p>
      </>
    ),
  },
];

// ─── Subprocessors ──────────────────────────────────────────────────
const SUBPROCESSORS = [
  {
    name:    'Supabase',
    purpose: 'Database, authentication, storage, edge functions',
    region:  'EU (Frankfurt)',
    where:   'supabase.com',
  },
  {
    name:    'Paystack',
    purpose: 'Card and mobile-money payment processing',
    region:  'Africa',
    where:   'paystack.com',
  },
  {
    name:    'Resend',
    purpose: 'Transactional email delivery',
    region:  'US / EU',
    where:   'resend.com',
  },
  {
    name:    'OpenTimestamps',
    purpose: 'Bitcoin-anchored timestamp proofs',
    region:  'Decentralized (Bitcoin network)',
    where:   'opentimestamps.org',
  },
];

// ─── Page ───────────────────────────────────────────────────────────

export default function SecurityPage() {
  return (
    <main style={{ background: BRAND.bgWhite, color: BRAND.textBody }}>

      {/* ════════ HERO ════════ */}
      <section
        className="pt-16 lg:pt-24 pb-12 relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${BRAND.bgWhite} 0%, ${BRAND.bgLight} 100%)` }}
      >
        <div
          aria-hidden
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[700px] pointer-events-none"
          style={{
            background: `radial-gradient(circle at 50% 0%, ${BRAND.blueGhost} 0%, transparent 55%)`,
            opacity: 0.6,
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
            style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
          >
            <Shield size={12} />
            Security &amp; trust
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-5"
            style={{ color: BRAND.textHead }}
          >
            Security is a feature, not a checkbox.
          </h1>
          <p className="text-lg sm:text-xl leading-relaxed" style={{ color: BRAND.textMuted }}>
            Your firm's data is yours, isolated from every other firm on the platform, encrypted
            in transit and at rest, and protected by audit-log + tamper-evident infrastructure.
            Here's exactly how.
          </p>
        </div>
      </section>

      {/* ════════ FOUR PILLARS ════════ */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PILLARS.map((p) => {
              const Ic = p.icon;
              return (
                <div
                  key={p.title}
                  className="p-6 rounded-2xl border"
                  style={{
                    background: BRAND.bgWhite,
                    borderColor: BRAND.border,
                    boxShadow: '0 8px 32px rgba(15,42,95,0.06)',
                  }}
                >
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                    style={{ background: BRAND.blueGhost, color: BRAND.blueDeep }}
                  >
                    <Ic size={18} />
                  </div>
                  <p className="text-base font-bold mb-2" style={{ color: BRAND.textHead }}>
                    {p.title}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: BRAND.textMuted }}>
                    {p.sub}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════ DETAILED SECTIONS ════════ */}
      <section className="py-16" style={{ background: BRAND.bgLight }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: BRAND.orangeDark }}>
              The full posture
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ color: BRAND.textHead }}>
              How we actually do it.
            </h2>
          </div>
          <div className="space-y-10">
            {SECTIONS.map((s) => {
              const Ic = s.icon;
              return (
                <div key={s.title} className="grid grid-cols-[44px_1fr] gap-4">
                  <div
                    className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: BRAND.bgWhite, color: BRAND.blueDeep, border: `1px solid ${BRAND.border}` }}
                  >
                    <Ic size={18} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-3" style={{ color: BRAND.textHead }}>
                      {s.title}
                    </h3>
                    <div className="space-y-3 text-sm leading-relaxed" style={{ color: BRAND.textBody }}>
                      {s.body}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ════════ SUBPROCESSORS ════════ */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: BRAND.orangeDark }}>
              Subprocessors
            </p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ color: BRAND.textHead }}>
              Every third party that touches your data.
            </h2>
            <p className="text-sm leading-relaxed max-w-2xl mx-auto" style={{ color: BRAND.textMuted }}>
              We're transparent about who we depend on. If we add a new subprocessor, you'll see it
              here first. Your data isn't shared with anyone not on this list.
            </p>
          </div>

          <div
            className="rounded-2xl overflow-hidden border"
            style={{ background: BRAND.bgWhite, borderColor: BRAND.border, boxShadow: '0 8px 32px rgba(15,42,95,0.06)' }}
          >
            <table className="w-full text-sm">
              <thead style={{ background: BRAND.bgLight }}>
                <tr>
                  <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND.textMuted }}>
                    Provider
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND.textMuted }}>
                    Purpose
                  </th>
                  <th className="text-left px-5 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: BRAND.textMuted }}>
                    Region
                  </th>
                </tr>
              </thead>
              <tbody>
                {SUBPROCESSORS.map((s, i) => (
                  <tr
                    key={s.name}
                    className={i % 2 === 0 ? '' : ''}
                    style={{ borderTop: `1px solid ${BRAND.borderSoft}` }}
                  >
                    <td className="px-5 py-3">
                      <p className="text-sm font-semibold" style={{ color: BRAND.textHead }}>
                        {s.name}
                      </p>
                      <p className="text-xs" style={{ color: BRAND.textFaint }}>
                        {s.where}
                      </p>
                    </td>
                    <td className="px-5 py-3 text-sm" style={{ color: BRAND.textBody }}>
                      {s.purpose}
                    </td>
                    <td className="px-5 py-3 text-xs" style={{ color: BRAND.textMuted }}>
                      <Globe2 size={11} className="inline mr-1.5" />
                      {s.region}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ════════ DISCLOSURE / COMPLIANCE ════════ */}
      <section className="py-16" style={{ background: BRAND.bgLight }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div
              className="p-6 rounded-2xl border"
              style={{ background: BRAND.bgWhite, borderColor: BRAND.border }}
            >
              <div
                className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
              >
                <AlertTriangle size={18} />
              </div>
              <h3 className="text-base font-bold mb-2" style={{ color: BRAND.textHead }}>
                Responsible disclosure
              </h3>
              <p className="text-sm leading-relaxed mb-3" style={{ color: BRAND.textMuted }}>
                Found a security issue? Email{' '}
                <a
                  href="mailto:security@docketflowapp.com"
                  className="font-semibold"
                  style={{ color: BRAND.blueDeep }}
                >
                  security@docketflowapp.com
                </a>
                . We acknowledge reports within one business day and we don't pursue legal action
                against good-faith researchers.
              </p>
            </div>

            <div
              className="p-6 rounded-2xl border"
              style={{ background: BRAND.bgWhite, borderColor: BRAND.border }}
            >
              <div
                className="w-11 h-11 rounded-lg flex items-center justify-center mb-4"
                style={{ background: BRAND.orangeSoft, color: BRAND.orangeDark }}
              >
                <ShieldCheck size={18} />
              </div>
              <h3 className="text-base font-bold mb-2" style={{ color: BRAND.textHead }}>
                Ghana DPA &amp; GDPR
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: BRAND.textMuted }}>
                We align with the Ghana Data Protection Act (2012) and the EU GDPR. Data subject
                access, deletion, and portability requests are honored within 30 days. Email{' '}
                <a
                  href="mailto:privacy@docketflowapp.com"
                  className="font-semibold"
                  style={{ color: BRAND.blueDeep }}
                >
                  privacy@docketflowapp.com
                </a>
                {' '}with any request.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ CTA ════════ */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl p-10 lg:p-14 text-center"
            style={{
              background: `linear-gradient(135deg, ${BRAND.blueDeep} 0%, ${BRAND.blueMid} 100%)`,
              color: '#fff',
              boxShadow: '0 24px 64px rgba(15,42,95,0.30)',
            }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-5">
              Want to dig deeper?
            </h2>
            <p
              className="text-base sm:text-lg max-w-xl mx-auto mb-8 leading-relaxed"
              style={{ color: 'rgba(226,232,240,0.85)' }}
            >
              Book a 20-minute call with our team. We'll walk through the security architecture in
              detail and answer questions from your IT or compliance contact.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => goTo('bookDemo')}
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm font-semibold rounded-xl w-full sm:w-auto"
                style={{
                  background: `linear-gradient(135deg, ${BRAND.orange} 0%, ${BRAND.orangeDark} 100%)`,
                  color: '#fff',
                  boxShadow: '0 8px 24px rgba(249,115,22,0.40)',
                }}
              >
                Book a security walkthrough
                <ArrowRight size={16} />
              </button>
              <WatchDemoButton variant="ghost" size="lg" label="Watch product demo (30s)" />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

// Helper for inline code formatting in body text.
function inlineCodeStyle() {
  return {
    background: BRAND.bgLighter,
    padding: '0.1em 0.4em',
    borderRadius: '4px',
    fontSize: '0.92em',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    color: BRAND.blueDeep,
  };
}
